import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, AuthState } from '../types';

interface AuthStore extends AuthState {
  // Actions
  signIn: (email: string, password: string) => Promise<boolean>;
  signInWithPin: (pin: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User | null) => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: false,
      error: null,

      signIn: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        console.log('🔑 Auth Store: Attempting email sign in:', email);
        
        // Demo email authentication
        if (email === 'picker@warehouse.com' && password === 'picker123') {
          const demoUser: User = {
            id: '550e8400-e29b-41d4-a716-446655440000',
            email: 'picker@warehouse.com',
            role: 'picker',
            firstName: 'Martin',
            lastName: 'Picker',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          
          console.log('🔑 Auth Store: Email sign in successful:', {
            id: demoUser.id,
            email: demoUser.email,
            role: demoUser.role
          });
          set({ user: demoUser, isLoading: false });
          return true;
        }
        
        console.log('🔑 Auth Store: Email sign in failed - invalid credentials');
        set({ error: 'Invalid credentials', isLoading: false });
        return false;
      },

      signInWithPin: async (pin: string) => {
        set({ isLoading: true, error: null });
        console.log('🔑 Auth Store: Attempting PIN sign in:', pin);
        
        // Demo PIN authentication
        if (pin === '1234') {
          const demoUser: User = {
            id: '550e8400-e29b-41d4-a716-446655440000',
            email: 'picker@warehouse.com',
            role: 'picker',
            firstName: 'Martin',
            lastName: 'Picker',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          
          console.log('🔑 Auth Store: PIN sign in successful:', {
            id: demoUser.id,
            email: demoUser.email,
            role: demoUser.role
          });
          set({ user: demoUser, isLoading: false });
          return true;
        }
        
        console.log('🔑 Auth Store: PIN sign in failed - invalid PIN');
        set({ error: 'Invalid PIN', isLoading: false });
        return false;
      },

      signOut: async () => {
        console.log('🔑 Auth Store: Signing out');
        localStorage.removeItem('auth-store');
        set({ user: null, isLoading: false, error: null });
      },

      clearError: () => set({ error: null }),

      setUser: (user: User | null) => set({ user })
    }),
    {
      name: 'auth-store',
      onRehydrateStorage: () => (state) => {
        // Fix invalid UUID format for demo user to match database
        if (state?.user?.id === 'demo-user-1' || state?.user?.id === 'a0eebc99-9c0b-4ef8-bb6d-6bb9bd380a11') {
          state.user.id = '550e8400-e29b-41d4-a716-446655440000';
        }
      },
      partialize: (state) => ({
        user: state.user
      })
    }
  )
);